import { subclass, property } from "esri/core/accessorSupport/decorators";
//@ts-ignore
import { formatDate, substitute } from "esri/intl";
import SearchViewModel = require("esri/widgets/Search/SearchViewModel");

import LayerSearchSource = require("esri/widgets/Search/LayerSearchSource");
import SpatialReference = require("esri/geometry/SpatialReference");

function hasProp(obj: any, property: string): boolean {
  return obj.hasOwnProperty(property) && obj[property] != null && obj[property] !== "";
}

const DEFAULT_SR = SpatialReference.WGS84;
const RE_HOSTED_FS = /https?:\/\/services.*\.arcgis\.com/i;

//@ts-ignore
import { AbortOptions } from "esri/core/promiseUtils";

// esri.widgets
//@ts-ignore
import { SuggestResult, SearchResult } from "esri/widgets/interfaces";

@subclass("app.route.core.SearchViewModel")
export default class CustomSearchViewModel extends SearchViewModel {
  //----------------------------------
  //  selectedResult
  //----------------------------------

  /**
   * The result selected from a search.
   *
   * @name selectedResult
   * @instance
   * @type {Object}
   * @default null
   * @readonly
   */
   @property({
    // readOnly: true // Core version
    readOnly: false // Indoors version
  })
  selectedResult: SearchResult = null;

  directions = false;
  tsUtil = null;

  constructor(properties?: any) {
    super(properties);

    this.directions = !!properties.directions;
    this.tsUtil = properties.tsUtil;
  }

  _getSuggestionsFromSource(
    suggestTerm: string,
    sourceIndex: number,
    options?: AbortOptions
  ): Promise<SuggestResult[]> {
    const source = this.allSources.getItemAt(sourceIndex);
    const suggestionsEnabled = hasProp(source, "suggestionsEnabled")
      ? source.suggestionsEnabled
      : this.suggestionsEnabled;
    const termLength = suggestTerm.length;
    const minSuggestCharacters = hasProp(source, "minSuggestCharacters")
      ? source.minSuggestCharacters
      : this.minSuggestCharacters;
    if (suggestionsEnabled && suggestTerm.trim() && termLength >= minSuggestCharacters) {
      const spatialReference = (this.view && this.view.spatialReference) || DEFAULT_SR;
      const maxSuggestions = hasProp(source, "maxSuggestions") ? source.maxSuggestions : this.maxSuggestions;
      const { view } = this;
      //@ts-ignore
      const exactMatch = source instanceof LayerSearchSource && hasProp(source, "exactMatch") ? source.exactMatch : false;

      //@ts-ignore
      const layer = source && source.layer;
      const fields = layer && layer.fields;
      if (fields && this.isPeopleLayer(fields)) {
        //@ts-ignore
        return this.getPeopleSuggestions(
          {
            source,
            view,
            sourceIndex,
            suggestTerm,
            spatialReference,
            maxSuggestions,
            exactMatch
          },
          //@ts-ignore
          options
        );
      } else {

        // Search: Sometimes expected results don't get returned in a search #5766
        try {
          //@ts-ignore
          if (source && layer && !exactMatch && source.declaredClass === "esri.widgets.Search.LayerSearchSource" &&
              typeof suggestTerm === "string") {
            // looks like the fix needs to be applied to both hosted and non-hosted layers
            // const isHostedFS = RE_HOSTED_FS.test(layer.url); 
            let term = suggestTerm.trim();
            if (term.length > 0 && term.indexOf("%") !== 0) {
              let allStringFields = false;
              //@ts-ignore
              const searchFields = source.searchFields || [this._getDisplayField(source)];
              if (Array.isArray(searchFields) && searchFields.length > 0) {
                allStringFields = searchFields.every(fieldName => {
                  if (fieldName) {
                    if (!layer.fieldsIndex) console.log("Search: no layer.fieldsIndex for",layer.title,layer)
                    const f = layer.fieldsIndex && layer.getField(fieldName);
                    return !!(f && f.type === "string");
                  }
                  return false;
                })
              }
              if (allStringFields) {
                // @todo temporary, turn off full text search, this could have secondary impacts
                if (layer.capabilities && layer.capabilities.query && layer.capabilities.query.supportsFullTextSearch) {
                  layer.capabilities.query.supportsFullTextSearch = false;
                }
                // @todo temporary, prefix the search term with a wildcard
                suggestTerm = "%"+term;
              }
            }
          }
        } catch(ex) {
          console.error(ex);
        }

        //@ts-ignore
        return source.getSuggestions(
          {
            view,
            sourceIndex,
            suggestTerm,
            spatialReference,
            maxSuggestions,
            exactMatch
          },
          //@ts-ignore
          options
        );
      }
    }

    //@ts-ignore
    return Promise.resolve(null);
  }

  getPeopleSuggestions(
    suggestionsParams: any,
    options: AbortOptions
  ): Promise<SuggestResult[]> {
    const { source, spatialReference, suggestTerm, maxSuggestions, sourceIndex, exactMatch } = suggestionsParams;
    const { layer, filter } = source;
    const signal = options && options.signal;

    return this._loadLayer(layer).then(() => {
      if (!this._supportsPagination(layer)) {
        return [];
      }

      const displayField = this._getDisplayField(source);
      //@ts-ignore
      const searchFields = source.searchFields || [displayField];
      const outFields: string[] = ["*"];

      const validDisplayField = this._isFieldValid(layer, displayField);
      const validSearchFields = this._hasValidFields(layer, searchFields);

      if (!validDisplayField) {
        console.error("invalid-field: displayField is invalid.");

        throw new Error("getSuggestions():invalid-field, displayField is invalid.");
      }

      if (!validSearchFields) {
        console.error("invalid-field: search field is invalid.");

        throw new Error("getSuggestions():invalid-field, search field is invalid.");
      }

      const q = layer.createQuery();
      //@ts-ignore
      const { orderByFields } = source;

      if (orderByFields) {
        q.orderByFields = orderByFields;
      }

      q.outSpatialReference = spatialReference;
      q.returnGeometry = false;
      q.num = maxSuggestions;
      q.outFields = outFields;

      const trimmedText = suggestTerm.trim();

      if (!trimmedText) {
        return [];
      }

      const { prefix = "", suffix = "" } = source;
      const suggestTemplate = this._fixSingleQuoteSQL(`${prefix}${suggestTerm}${suffix}`);

      const where = this._getWhereSQL({
        searchTerm: suggestTemplate,
        layer,
        filter,
        searchFields,
        exactMatch,
        type: "suggest"
      });

      if (!where) {
        return [];
      }

      q.where = where;

      return layer
        .queryFeatures(q, { signal })
        .then((featureSet) => this._filterPeople(featureSet))
        .then((featureSet) => this._hydrateLayerSuggestions(featureSet, source, sourceIndex, displayField));
    });
  }

  //@ts-ignore
  async _filterPeople(featureSet: any): Promise<any> {
    const results: any[] = [];

    for (let i = 0; i < featureSet.features.length; i++) {
    // featureSet.features.forEach((feature: any) => {
      const feature = featureSet.features[i];
      const { attributes } = feature;
      const email = this.getAttributeValue(attributes, "email");
      //@ts-ignore
      const existingRecord = results.find((f: any) => {
        const { attributes } = f;
        const sEmail = this.getAttributeValue(attributes, "email");
        return sEmail === email;
      });
      const hasLKL = this.tsUtil.tsGetLKL(featureSet.features);
      const hasUnitAssignment = this.tsUtil.hasUnitAssignment(feature);

      let upcomingReservations = [];
      // if (!existingRecord && this.directions) {
      //   upcomingReservations = await this.tsUtil.tsFindUserReservations({ feature }) || [];
      // }

      const clauses = [
        !existingRecord,
        hasUnitAssignment || hasLKL || upcomingReservations.length > 0
      ]
      // #4434 Note: If we do something like this, the performance will be bad when searching for people
      // for directions. Possibly better to not filter the people this way at all? A person can have
      // an upcoming/ongoing reservation, but not be assigned to any units and therefore wouldn't
      // show up in the list with our current geometry filters. We either need to show that person no 
      // matter what or make a query for reservations everytime they are searched...
      
      // const canShow = this.directions ? clauses[0] && clauses[1] : clauses[0];
      const canShow = clauses[0];

      if (canShow) {
        results.push(feature);
      }
    }

    return { features: results };
  }

  getAttributeValue(attributes, name) {
    if (!attributes || !name) { return; }
    if (typeof name !== "string") { return; }
    if (attributes.hasOwnProperty(name)) { return attributes[name]; }
    let value, lc = name.toLowerCase();
    Object.keys(attributes).some(key => {
      if (key.toLowerCase() === lc) {
        value = attributes[key];
        return true;
      }
      return false;
    });
    return value;
  }

  isPeopleLayer(fields) {
    if (fields) {
      let count = 0;
      const matched = fields.some((field) => {
        const lc = field.name.toLowerCase();
        if (lc === "knownas" ||
            lc === "email") {
          count++;
          if (count >= 2) { return true; }
        }
        return false;
      });
      return matched;
    }
    return false;
  }

  _getFirstStringField(layer: any): string {
    const field = (
      layer &&
      layer.fieldsIndex &&
      layer.fieldsIndex.fields &&
      layer.fieldsIndex.fields.find((field) => field.type === "string")
    );

    return (field && field.name) || "";
    // return layer?.fieldsIndex?.fields?.find((field) => field.type === "string")?.name ?? "";
  }

  _getDisplayField(source: any): string {
    return source.displayField || source.layer.displayField || this._getFirstStringField(source.layer);
  }

  _fixSingleQuoteSQL(searchTerm: string): string {
    // replace single ' with '' for valid where clause
    return searchTerm.replace(/\'/g, "''");
  }

  _getWhereSQL({
    searchTerm,
    layer,
    searchFields,
    filter,
    exactMatch,
    type
  }: {
    searchTerm: string;
    layer: any;
    searchFields: string[];
    filter: any;
    exactMatch: boolean;
    type: any;
  }): string {
    const { definitionExpression, url } = layer;

    let where: string = "";

    if (searchTerm && searchFields) {
      searchFields.forEach((searchField) => {
        const field = layer.getField(searchField);
        const domain = typeof layer.getFieldDomain === "function" && layer.getFieldDomain(searchField);
        const domainCodedValue =
          domain && domain.type === "coded-value"
            ? this._getCodedValue(domain, searchTerm, exactMatch)
            : null;
        const currentTerm = domainCodedValue || searchTerm || null;
        if (currentTerm !== null) {
          const sql = this._getSQLStatement({ currentTerm, field, filter, exactMatch, url, type });
          if (sql) {
            where += this._combineSQLStatements(where, sql);
          }
        }
      });
    }

    return definitionExpression ? `(${definitionExpression}) AND (${where})` : where;
  }

  _applyExpressionToStatement(statement: string, filter: any): string {
    const filterWhere = filter && filter.where;
    return filterWhere ? `(${statement}) AND (${filterWhere})` : statement;
  }

  _getSQLStatement({
    currentTerm,
    field,
    filter,
    exactMatch,
    url,
    type
  }: {
    currentTerm: string;
    field: any;
    filter: any;
    exactMatch: boolean;
    url: string;
    type: any;
  }): string {
    const fieldType = field.type;
    const fieldName = field.name;
    // string, date, or global-id field
    if (fieldType === "string" || fieldType === "date" || fieldType === "global-id") {
      const isHostedFS = RE_HOSTED_FS.test(url);
      const nlc = isHostedFS && this._containsNonLatinCharacter(currentTerm) ? "N" : "";

      if (exactMatch && type === "search") {
        return this._applyExpressionToStatement(`${fieldName} = ${nlc}'${currentTerm}'`, filter);
      }

      const fieldNameCasing = !isHostedFS ? `UPPER(${fieldName})` : fieldName;
      const currentTermCasing = !isHostedFS ? currentTerm.toUpperCase() : currentTerm;
      const wildcard = exactMatch ? `'${currentTermCasing}%'` : `'%${currentTermCasing}%'`;

      return this._applyExpressionToStatement(`${fieldNameCasing} LIKE ${nlc}${wildcard}`, filter);
    }
    // number field
    if (
      fieldType === "oid" ||
      fieldType === "small-integer" ||
      fieldType === "integer" ||
      fieldType === "single" ||
      fieldType === "double"
    ) {
      const num = Number(currentTerm);

      if (isNaN(num)) {
        return null;
      }

      return this._applyExpressionToStatement(`${fieldName} = ${num}`, filter);
    }
    // exact match
    return this._applyExpressionToStatement(`${fieldName} = ${currentTerm}`, filter);
  }

  _combineSQLStatements(where: string, sql: string): string {
    return where ? ` OR (${sql})` : `(${sql})`;
  }

  _containsNonLatinCharacter(searchTerm: string): boolean {
    for (let i = 0; i < searchTerm.length; i++) {
      if (searchTerm.charCodeAt(i) > 255) {
        return true;
      }
    }
    return false;
  }

  _getCodedValue(domain: any, name: string, exactMatch: boolean): string {
    let code: string = null;
    const { codedValues } = domain;
    if (codedValues) {
      codedValues.some((codedValue) => {
        const currentName = codedValue.name;
        const matchCurrentName = exactMatch ? currentName : currentName.toLowerCase();
        const matchName = exactMatch ? name : name.toLowerCase();
        if (matchName === matchCurrentName) {
          code = codedValue.code.toString();
          return true;
        }
        return false;
      });
    }
    return code;
  }

  //@ts-ignore
  async _loadLayer(layer: any): Promise<void> {
    if (!layer) {
      return;
    }

    await layer.load();
  }

  _supportsPagination(layer: any): boolean {
    return (
      layer &&
      layer.capabilities &&
      layer.capabilities.query &&
      layer.capabilities.query.supportsPagination
    ) || false;
    // return layer?.capabilities?.query?.supportsPagination ?? false;
  }

  _hydrateLayerSuggestion(
    feature: any,
    source: any,
    sourceIndex: number,
    displayField: string
  ): SuggestResult {
    const layer = feature.sourceLayer;
    const { attributes } = feature;
    const { objectIdField } = layer;
    const key = attributes[objectIdField];
    const text = this._getLayerFeatureText(feature, source.suggestionTemplate, displayField);
    return {
      text,
      attributes,
      key,
      sourceIndex
    };
  }

  _hydrateLayerSuggestions(
    featureSet: any,
    source: any,
    sourceIndex: number,
    displayField: string
  ): SuggestResult[] {
    return featureSet.features.map((feature) => this._hydrateLayerSuggestion(feature, source, sourceIndex, displayField));
  }

  _getCodedName(domain: any, codedValue: string): string {
    let name: string = null;
    const { codedValues } = domain;
    if (codedValues && codedValues.length) {
      codedValues.some((value) => {
        if (value.code === codedValue) {
          name = value.name;
          return true;
        }
        return false;
      });
    }
    return name;
  }

  getValueCaseInsensitive(object: object, key: string): any {
    //@ts-ignore
    return object[Object.keys(object).find((k) => k.toLowerCase() === key.toLowerCase())];
  }

  _getLayerFeatureText(feature: any, template: string, displayField: string): string {
    const layer = feature.sourceLayer;
    const { attributes } = feature;
    const domain = typeof layer.getFieldDomain === "function" && layer.getFieldDomain(displayField);

    if (template) {
      return substitute(template, attributes);
    }

    if (displayField && attributes) {
      const field = layer.getField(displayField);
      const dfValue = this.getValueCaseInsensitive(attributes, displayField);

      if (dfValue == null) {
        return "";
      }

      if (domain && domain.type === "coded-value") {
        return this._getCodedName(domain, dfValue);
      }

      if (field && field.type === "date") {
        return formatDate(new Date(dfValue));
      }

      if (typeof dfValue === "number") {
        return dfValue.toString();
      }

      if (typeof dfValue !== "string") {
        return "";
      }

      return dfValue.trim();
    }
  }

  _hasValidFields(layer: any, fields: string[]): boolean {
    if (!layer || !fields) {
      return false;
    }

    return fields.every((field) => this._isFieldValid(layer, field));
  }

  _isFieldValid(layer: any, field: string): boolean {
    return !!layer.getField(field);
  }
}
